import { useState } from "react";
import { Link } from "wouter";
import {
  useDeviceOperation,
  useEjectRepair,
  useFaultyBatteries,
  useBatteryRotations,
  type BatteryRow,
} from "../lib/api.js";
import { StatusPill } from "../components/StatusPill.js";
import { parseFaultCause, isGhostBatteryId } from "../lib/faultText.js";

const faultLabel: Record<number, string> = {
  0: "ok",
  1: "virtual power",
  2: "Apple connector",
  3: "USB connector",
  4: "USB-C",
  5: "swelling",
  6: "auth failed",
  7: "cannot charge",
  8: "duplicate id",
  9: "recall",
  10: "health substandard",
  11: "overuse limit",
};


function FaultyRow({
  b,
  expanded,
  onToggle,
  rotations,
}: {
  b: BatteryRow;
  expanded: boolean;
  onToggle: () => void;
  rotations: number;
}) {
  const repair = useEjectRepair();
  const op = useDeviceOperation();
  const t = (b.faultType ?? 0) >= 7 ? "bad" : "warn";
  const ghost = isGhostBatteryId(b.id);
  const cause = parseFaultCause(b.faultCause);
  return (
    <>
      <tr
        onClick={onToggle}
        className="cursor-pointer border-b border-zinc-800/60 transition hover:bg-zinc-900/40 last:border-b-0"
      >
        <td className="px-4 py-3 font-mono text-xs">
          {ghost ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-zinc-800/60 px-2 py-0.5 text-zinc-400 ring-1 ring-zinc-700">
              <span aria-hidden>👻</span> ghost battery
            </span>
          ) : (
            <Link
              href={`/batteries/${b.id}`}
              onClick={(e) => e.stopPropagation()}
              className="text-zinc-200 hover:text-brand-orange"
            >
              {b.id}
            </Link>
          )}
        </td>
        <td className="px-4 py-3 font-mono text-xs text-zinc-400">
          {b.cabinetId ? (
            <Link
              href={`/cabinets/${b.cabinetId}`}
              onClick={(e) => e.stopPropagation()}
              className="hover:text-zinc-200"
            >
              {b.cabinetId}
            </Link>
          ) : (
            "—"
          )}
        </td>
        <td className="px-4 py-3 font-mono text-xs text-zinc-400">{b.slot ?? "—"}</td>
        <td className="px-4 py-3 font-mono text-xs text-zinc-400">
          {b.vol !== null ? `${b.vol}%` : "—"}
        </td>
        <td className="px-4 py-3 font-mono text-xs text-zinc-400">{rotations}</td>
        <td className="px-4 py-3">
          <StatusPill tone={t}>
            {faultLabel[b.faultType ?? 0] ?? `fault ${b.faultType}`}
          </StatusPill>
          {cause.reason && (
            <div className="mt-1 text-xs text-zinc-400">
              {cause.reason}
              {cause.detectedAt && (
                <span className="ml-2 text-[11px] text-zinc-600">
                  · since {cause.detectedAt}
                </span>
              )}
            </div>
          )}
        </td>
        <td className="px-4 py-3 text-right">
          {ghost ? (
            b.cabinetId && b.slot !== null ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  op.mutate({
                    cabinetId: b.cabinetId!,
                    operationType: "pop",
                    slotNum: b.slot!,
                    reason: "ghost-battery",
                  });
                }}
                disabled={op.isPending}
                className="btn-danger"
              >
                Pop slot {b.slot}
              </button>
            ) : (
              <span className="text-[11px] text-zinc-600">no cabinet</span>
            )
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                repair.mutate({ batteryId: b.id });
              }}
              disabled={repair.isPending}
              className="btn-danger"
            >
              Eject for repair
            </button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} className="bg-zinc-950/40 px-4 py-3">
            <div className="flex flex-wrap gap-2">
              {!ghost && (
                <Link href={`/batteries/${b.id}`} className="btn-secondary">
                  Battery lifecycle →
                </Link>
              )}
              {b.cabinetId && (
                <Link href={`/cabinets/${b.cabinetId}`} className="btn-secondary">
                  Open cabinet →
                </Link>
              )}
              {b.cabinetId && b.slot !== null && !ghost && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    op.mutate({
                      cabinetId: b.cabinetId!,
                      operationType: "lockStopCharge",
                      slotNum: b.slot!,
                      reason: "fault",
                    });
                  }}
                  disabled={op.isPending}
                  className="btn-purple"
                >
                  Lock + stop charge slot {b.slot}
                </button>
              )}
            </div>
            <p className="mt-2 text-[11px] leading-relaxed text-zinc-500">
              {ghost ? (
                <>
                  <strong className="text-zinc-300">Ghost battery:</strong> Bajie&apos;s
                  hardware can&apos;t read the chip in this slot — battery ID comes back
                  all-zeros. The battery is physically present but unidentifiable, so it
                  can&apos;t be tracked, billed, or repaired in the normal flow. The fix is
                  to <strong>Pop slot {b.slot ?? "?"}</strong> on this cabinet so a tech can
                  retrieve it manually for inspection.
                </>
              ) : (
                <>
                  <strong>Eject for repair</strong> uses Bajie&apos;s dedicated repair
                  endpoint — flags the battery in their backend and pops the slot. Use{" "}
                  <strong>Lock + stop charge</strong> for swelling or thermal events that
                  shouldn&apos;t be ejected to a customer slot.
                </>
              )}
            </p>
          </td>
        </tr>
      )}
    </>
  );
}

export function FaultyBatteries() {
  const { data, isLoading, error } = useFaultyBatteries();
  const { data: rotations } = useBatteryRotations(500);
  const rows = data ?? [];
  const rotationMap = new Map((rotations ?? []).map((r) => [r.batteryId, r.rotations]));
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-50">Faulty batteries</h1>
        <p className="mt-1 text-sm text-zinc-500">
          Every battery currently reporting a non-zero fault. "Eject for repair" calls
          Bajie's <code className="font-mono text-xs text-zinc-400">/cabinet/repairEject</code>{" "}
          to mark the battery as repair-mode and pop it for service.
        </p>
      </header>

      {error && (
        <div className="card p-6 text-sm text-rose-300">
          Failed to load: {(error as Error).message}
        </div>
      )}

      {isLoading && <div className="card p-10 text-center text-sm text-zinc-500">Loading…</div>}

      {!isLoading && rows.length === 0 && (
        <div className="card p-10 text-center text-sm text-zinc-400">
          🎉 No faulty batteries right now.
        </div>
      )}

      {rows.length > 0 && (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800/60 text-left text-[11px] uppercase tracking-wider text-zinc-500">
                <th className="px-4 py-3 font-medium">Battery</th>
                <th className="px-4 py-3 font-medium">Cabinet</th>
                <th className="px-4 py-3 font-medium">Slot</th>
                <th className="px-4 py-3 font-medium">Charge</th>
                <th className="px-4 py-3 font-medium">Rotations</th>
                <th className="px-4 py-3 font-medium">Fault</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <FaultyRow
                  key={b.id}
                  b={b}
                  expanded={expandedId === b.id}
                  onToggle={() =>
                    setExpandedId((prev) => (prev === b.id ? null : b.id))
                  }
                  rotations={rotationMap.get(b.id) ?? 0}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
